import React from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Database,
  FileArchive,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { Badge } from "./Primitives";
import { useLocale } from "../lib/i18n";

const SampleRow = ({ sample, active, onSelect }) => {
  const score = Math.max(...sample.runs.map((run) => run.effectiveScore ?? 0), 0);
  const state = sample.analysis.passed ? "pass" : "fail";
  return (
    <button
      type="button"
      className={`sample-row ${active ? "sample-row-active" : ""}`}
      onClick={() => onSelect(sample.id)}
    >
      <span className={`sample-dot sample-dot-${state}`} />
      <span className="sample-row-copy">
        <strong>{sample.id}</strong>
        <small>{sample.category.at(-1) || sample.title}</small>
      </span>
      <span className={`mini-score ${score >= 0.5 ? "mini-high" : score >= 0.3 ? "mini-target" : "mini-low"}`}>
        {sample.runs.length ? score.toFixed(2) : "—"}
      </span>
    </button>
  );
};

export default function Sidebar({
  workspace,
  selectedId,
  onSelect,
  query,
  onQuery,
  onOpenDirectory,
  onOpenArchive,
  onLoadLocal,
  onLoadPublished,
  loading,
  publishedLoading,
}) {
  const { t } = useLocale();
  const samples = workspace?.samples || [];
  const filtered = samples.filter((sample) =>
    `${sample.id} ${sample.title} ${sample.category.join(" ")}`.toLowerCase().includes(query.toLowerCase()),
  );
  const passing = samples.filter((sample) => sample.analysis.passed).length;
  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">
          <span />
          <span />
          <span />
        </div>
        <div>
          <strong>HARBOR LENS</strong>
          <small>{t("brand.tagline")}</small>
        </div>
      </div>

      <div className="sidebar-actions">
        <div className="sidebar-import-grid">
          <button type="button" className="primary-action" onClick={onOpenDirectory}><Upload size={15} />{t("sidebar.directory")}</button>
          <button type="button" className="secondary-action" onClick={onOpenArchive}><FileArchive size={15} />{t("sidebar.bundle")}</button>
        </div>
        <button type="button" className="secondary-action" onClick={onLoadLocal} disabled={loading}>
          <RefreshCw size={15} className={loading ? "spin" : ""} /> {t("sidebar.loadProject")}
        </button>
        <button type="button" className="secondary-action" onClick={onLoadPublished} disabled={publishedLoading}>
          <FileArchive size={15} className={publishedLoading ? "spin" : ""} /> {publishedLoading ? t("sidebar.loadingPublished") : t("sidebar.loadPublished")}
        </button>
        <small className="published-public-note">{t("sidebar.publishedPublic")}</small>
      </div>

      <div className="privacy-note">
        <ShieldCheck size={15} />
        <span>{t("sidebar.privacy")}</span>
      </div>

      {workspace ? (
        <div className="sidebar-stats">
          <div><Database size={13} /><strong>{samples.length}</strong><span>{t("sidebar.samples")}</span></div>
          <div><Activity size={13} /><strong>{workspace.stats.runCount}</strong><span>{t("sidebar.trajectories")}</span></div>
          <div><CheckCircle2 size={13} /><strong>{passing}</strong><span>{t("sidebar.staticPass")}</span></div>
          <div><AlertTriangle size={13} /><strong>{workspace.stats.errors}</strong><span>{t("sidebar.errors")}</span></div>
        </div>
      ) : null}

      <div className="search-box">
        <Search size={14} />
        <input value={query} onChange={(event) => onQuery(event.target.value)} placeholder={t("sidebar.search")} />
      </div>

      <div className="sample-list-head">
        <span>{t("sidebar.localSamples")}</span>
        <Badge>{filtered.length}</Badge>
      </div>
      <div className="sample-list">
        {filtered.map((sample) => (
          <SampleRow
            key={sample.id}
            sample={sample}
            active={sample.id === selectedId}
            onSelect={onSelect}
          />
        ))}
        {!filtered.length ? (
          <div className="sidebar-empty"><FileArchive size={18} />{t("sidebar.waiting")}</div>
        ) : null}
      </div>
      <div className="sidebar-footer">{t("sidebar.profile")}</div>
    </aside>
  );
}
